import { Navigate, Route, Routes, NavLink, useLocation } from 'react-router-dom';
import { Fingerprint, LayoutDashboard, Building2, Users, Bot } from 'lucide-react';
import { lazy, Suspense } from 'react';
import { AppScopeBanner } from '@/components/AppScopeBanner';

const EntityDashboard = lazy(() => import('./EntityDashboard'));
const EntityRegistry = lazy(() => import('./EntityRegistry'));
const EntityWizard = lazy(() => import('./EntityWizard'));
const EntityDetailPage = lazy(() => import('./EntityDetailPage'));
const UsersPage = lazy(() => import('./UsersPage'));
const IdentityAgentTab = lazy(() => import('./IdentityAgentTab'));

const NAV_ITEMS = [
  { label: 'Dashboard', path: '', icon: LayoutDashboard },
  { label: 'Entities', path: 'registry', icon: Building2 },
  { label: 'Users', path: 'users', icon: Users },
  { label: 'Agent', path: 'agent', icon: Bot },
] as const;

function NavBar() {
  const location = useLocation();
  const base = '/entity';
  const sub = location.pathname.replace(base, '').replace(/^\//, '').split('/')[0] || '';

  return (
    <div className="flex items-center gap-1 px-6 pt-5 pb-2">
      <div className="flex items-center gap-2 mr-6">
        <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
          <Fingerprint className="w-4 h-4 text-white" />
        </div>
        <span className="text-lg font-semibold text-gray-900">Identity</span>
      </div>

      <nav className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
        {NAV_ITEMS.map((item) => {
          const isActive = sub === item.path || (item.path === '' && sub === '');
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path === '' ? base : `${base}/${item.path}`}
              end={item.path === ''}
              className={`
                flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors
                ${isActive
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}
              `}
            >
              <Icon className="w-3.5 h-3.5" />
              {item.label}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}

function FallbackSpinner() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
    </div>
  );
}

export default function EntityRouter() {
  return (
    <div className="h-full flex flex-col">
      <NavBar />
      <div className="px-6 pt-2">
        <AppScopeBanner />
      </div>
      <div className="flex-1 min-h-0 px-6 pb-6 overflow-auto">
        <Suspense fallback={<FallbackSpinner />}>
          <Routes>
            <Route index element={<EntityDashboard />} />
            <Route path="registry" element={<EntityRegistry />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="agent" element={<IdentityAgentTab />} />
            <Route path="wizard" element={<EntityWizard />} />
            <Route path=":id" element={<EntityDetailPage />} />
            <Route path="*" element={<Navigate to="/entity" replace />} />
          </Routes>
        </Suspense>
      </div>
    </div>
  );
}
